import React from 'react';

/**
 * CriteriaChecklist — shared component for ASC 606 criterion review.
 *
 * Each criterion row renders a labeled checkbox with a required-on-true
 * justification textarea. Designed for reuse across Phase 7b (Step 1 review),
 * Phase 8 (distinctness / principal-vs-agent), and Phase 9 (variable consideration).
 *
 * The component is controlled — callers own the values Map and update it via
 * onChange. No internal state; the form decides when to validate.
 */

export interface CriterionDef {
  key: string;
  label: string;
  description?: string;
  notesPlaceholder?: string;
}

export interface CriterionValue {
  value: boolean | null;
  notes: string;
}

interface CriteriaChecklistProps {
  criteria: CriterionDef[];
  values: Record<string, CriterionValue>;
  onChange: (key: string, next: CriterionValue) => void;
  disabled?: boolean;
  showNotes?: boolean;
}

export default function CriteriaChecklist({
  criteria,
  values,
  onChange,
  disabled = false,
  showNotes = true,
}: CriteriaChecklistProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {criteria.map((c) => {
        const current = values[c.key] || { value: null, notes: '' };
        return (
          <div
            key={c.key}
            style={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              padding: 12,
              background: 'var(--bg-surface)',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: disabled ? 'default' : 'pointer' }}>
              <input
                type="checkbox"
                checked={current.value === true}
                disabled={disabled}
                onChange={(e) => onChange(c.key, { ...current, value: e.target.checked })}
                style={{ marginTop: 2, cursor: disabled ? 'default' : 'pointer', width: 16, height: 16 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.label}</div>
                {c.description && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{c.description}</div>
                )}
              </div>
            </label>
            {showNotes && (
              <textarea
                value={current.notes}
                disabled={disabled}
                onChange={(e) => onChange(c.key, { ...current, notes: e.target.value })}
                placeholder={c.notesPlaceholder || 'Notes (evidence / rationale)...'}
                style={{
                  width: '100%',
                  padding: 6,
                  marginTop: 8,
                  fontSize: 12,
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 4,
                  color: 'var(--text-primary)',
                  resize: 'vertical',
                  minHeight: 40,
                  fontFamily: 'inherit',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
